import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Truck, Package, DollarSign, Clock, Store } from 'lucide-react';
import PickupSelector from './PickupSelector';
import './ShippingSelector.css';

function ShippingSelector({ subtotal, onShippingChange }) {
  const [shippingMethods, setShippingMethods] = useState([]);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [shippingType, setShippingType] = useState('delivery'); // 'delivery' or 'pickup'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadShippingSettings();
  }, []);

  useEffect(() => {
    calculateShipping();
  }, [selectedMethod, selectedPickup, subtotal, freeShippingThreshold, shippingType]);

  const loadShippingSettings = async () => {
    try {
      // Load shipping methods
      const shippingSnapshot = await getDocs(collection(db, 'shippingMethods'));
      const methods = shippingSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(method => method.active)
        .sort((a, b) => a.baseRate - b.baseRate);
      
      setShippingMethods(methods);
      
      // Load free shipping threshold
      const settingsDoc = await getDocs(collection(db, 'settings'));
      const threshold = settingsDoc.docs.find(doc => doc.id === 'shipping')?.data()?.freeShippingThreshold || 0;
      setFreeShippingThreshold(threshold);
      
      // Select cheapest method by default
      if (methods.length > 0) {
        setSelectedMethod(methods[0]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading shipping settings:', error);
      setLoading(false);
    }
  };

  const calculateShipping = () => {
    if (shippingType === 'pickup' && selectedPickup) {
      // Pickup is always free
      onShippingChange({ 
        type: 'pickup',
        method: selectedPickup.location, 
        cost: 0,
        estimatedReady: selectedPickup.estimatedReady
      });
      return;
    }

    if (!selectedMethod || shippingType === 'pickup') {
      onShippingChange({ method: null, cost: 0 });
      return;
    }

    let shippingCost = 0;
    let shippingMethod = selectedMethod;

    // Check for free shipping
    if (freeShippingThreshold > 0 && subtotal >= freeShippingThreshold) {
      shippingCost = 0;
      shippingMethod = { ...selectedMethod, name: 'Free Shipping', baseRate: 0 };
    } else {
      shippingCost = selectedMethod.baseRate;
    }

    onShippingChange({ 
      type: 'delivery',
      method: shippingMethod, 
      cost: shippingCost 
    });
  };

  const getShippingIcon = (carrier) => {
    switch (carrier?.toLowerCase()) {
      case 'usps':
        return '📬';
      case 'fedex':
        return '🚚';
      case 'dhl':
        return '✈️';
      case 'ups':
        return '📦';
      default:
        return '🚛';
    }
  };

  const handlePickupSelection = (pickupData) => {
    setSelectedPickup(pickupData);
    setSelectedMethod(null); // Clear delivery selection
    setShippingType('pickup');
  };

  const handleDeliverySelection = () => {
    setShippingType('delivery');
    setSelectedPickup(null); // Clear pickup selection
  };

  const getAmountForFreeShipping = () => {
    if (freeShippingThreshold <= 0) return null;
    const remaining = freeShippingThreshold - subtotal;
    return remaining > 0 ? remaining : 0;
  };

  if (loading) {
    return (
      <div className="shipping-selector">
        <div className="shipping-header">
          <h3><Truck size={20} /> Shipping Method</h3>
        </div>
        <div className="loading-shipping">
          <div className="spinner"></div>
          <p>Loading shipping options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shipping-selector">
      <div className="shipping-header">
        <h3><Truck size={20} /> Shipping Method</h3>
        {getAmountForFreeShipping() > 0 && (
          <div className="free-shipping-reminder">
            <DollarSign size={16} />
            Add ${getAmountForFreeShipping().toFixed(2)} more for free shipping!
          </div>
        )}
      </div>

      {/* Shipping Type Selection */}
      <div className="shipping-type-tabs">
        <button 
          className={`shipping-tab ${shippingType === 'delivery' ? 'active' : ''}`}
          onClick={handleDeliverySelection}
        >
          <Truck size={16} />
          Delivery
        </button>
        <button 
          className={`shipping-tab ${shippingType === 'pickup' ? 'active' : ''}`}
          onClick={() => setShippingType('pickup')}
        >
          <Store size={16} />
          Local Pickup (NY)
        </button>
      </div>

      {/* Delivery Options */}
      {shippingType === 'delivery' && (
        <>
          {shippingMethods.length === 0 ? (
            <div className="no-shipping">
              <Package size={48} />
              <p>No shipping methods available</p>
            </div>
          ) : (
            <div className="shipping-options">
              {shippingMethods.map((method) => {
                const isFree = freeShippingThreshold > 0 && subtotal >= freeShippingThreshold;
                const displayCost = isFree ? 0 : method.baseRate;
                const displayName = isFree ? 'Free Shipping' : method.name;
                
                return (
                  <div
                    key={method.id}
                    className={`shipping-option ${selectedMethod?.id === method.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedMethod(method);
                      setSelectedPickup(null);
                    }}
                  >
                    <div className="shipping-option-content">
                      <div className="shipping-option-left">
                        <div className="shipping-icon">
                          {getShippingIcon(method.carrier)}
                        </div>
                        <div className="shipping-info">
                          <div className="shipping-name">{displayName}</div>
                          <div className="shipping-details">
                            <span className="carrier">{method.carrier}</span>
                            <span className="separator">•</span>
                            <span className="delivery">
                              <Clock size={12} />
                              {method.estimatedDays}
                            </span>
                          </div>
                          {method.description && (
                            <div className="shipping-description">{method.description}</div>
                          )}
                        </div>
                      </div>
                      <div className="shipping-option-right">
                        <div className="shipping-price">
                          {displayCost === 0 ? (
                            <span className="free-price">FREE</span>
                          ) : (
                            <span>${displayCost.toFixed(2)}</span>
                          )}
                        </div>
                        <div className="shipping-radio">
                          <div className={`radio-dot ${selectedMethod?.id === method.id ? 'checked' : ''}`}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Pickup Options */}
      {shippingType === 'pickup' && (
        <PickupSelector 
          selected={selectedPickup}
          onSelectionChange={handlePickupSelection}
          subtotal={subtotal}
        />
      )}

      {freeShippingThreshold > 0 && shippingType === 'delivery' && (
        <div className="free-shipping-info">
          <div className="free-shipping-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${Math.min((subtotal / freeShippingThreshold) * 100, 100)}%` }}
              ></div>
            </div>
            <div className="progress-text">
              <span>
                ${subtotal.toFixed(2)} of ${freeShippingThreshold.toFixed(2)} for free shipping
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShippingSelector;
